import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  IconDownload,
  IconMail,
  Secondary,
  Stack,
  Strong,
  Text,
  TextLinkButton,
} from 'braid-design-system';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';

import translations from './.vocab';
import type { ProfileInsightsActivity } from './types';

const ActivityItemDescription = ({
  activity,
}: {
  activity: ProfileInsightsActivity;
}) => {
  const { t } = useTranslations(translations);
  const { profileInsightsAnalyticsTracker } = useAnalytics();
  const { name: advertiserName } = activity.advertiser!;

  const onClickJobUrl = (jobUrl: string) => {
    profileInsightsAnalyticsTracker({
      eventName: 'profile_insights_activity_pressed',
      props: {
        currentPage: 'profile talent search insights view',
        currentPageSection: 'talent search insights',
        currentPageSectionState: 'view',
      },
    });

    window.open(`${jobUrl}?ref=profile-recom`, '_blank');
  };

  switch (activity.__typename) {
    case 'ProfileInsightsMessageSentActivity2':
      return (
        <Text>
          {t('advertiserName sent you a message to your email', {
            advertiserName,
          })}
        </Text>
      );
    case 'ProfileInsightsResumeDownloadedActivity2':
      return (
        <Text>
          {t('advertiserName downloaded your profile information', {
            advertiserName,
          })}
        </Text>
      );
    case 'ProfileInsightsJobSentActivity2':
      if ('job' in activity) {
        const { title: jobTitle, url: jobUrl, id: jobId } = activity.job!;
        return (
          <Text>
            {t('advertiserName invited you to apply for jobTitle', {
              advertiserName,
              jobTitle,
              TextLinkButton: (children) => (
                <TextLinkButton
                  data={{
                    automation: `profile-insights-activities-job-${jobId}`,
                  }}
                  onClick={() => onClickJobUrl(jobUrl)}
                >
                  {children}
                </TextLinkButton>
              ),
            })}
          </Text>
        );
      }
      return null;

    default:
      return null;
  }
};

const ActivityItem = ({ activity }: { activity: ProfileInsightsActivity }) => {
  const { date } = activity;
  return (
    <Columns space="small">
      <Column width="content">
        {activity.__typename === 'ProfileInsightsResumeDownloadedActivity' ? (
          <IconDownload tone="secondary" size="small" />
        ) : (
          <IconMail tone="secondary" size="small" />
        )}
      </Column>
      <Column>
        <Stack space="small">
          <ActivityItemDescription activity={activity} />
          <Text size="small">
            <Secondary>{date.shortLabel}</Secondary>
          </Text>
        </Stack>
      </Column>
    </Columns>
  );
};

export const ActivitiesSection = ({
  activities,
}: {
  activities: ProfileInsightsActivity[];
}) => {
  const { t } = useTranslations(translations);

  return (
    <Box data={{ automation: 'profile-insights-drawer-activities' }}>
      <Stack space="large">
        <Text size="xsmall">
          <Secondary>
            <Strong>{t('Activity')}</Strong>
          </Secondary>
        </Text>
        <Stack space="xlarge">
          {activities.map((activity, index) => (
            <ActivityItem activity={activity} key={`activity-${index}`} />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};
